import { useState, useEffect } from "react";
import { Card } from "@tremor/react";
import { useInvestigations, useInvestigation } from "@/lib/queries";
import { AuditMarkdown } from "@/components/AuditMarkdown";
import { cn } from "@/lib/utils";

function fmtDateTime(iso: string): string {
  return new Date(iso).toLocaleString([], {
    year: "numeric", month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function fmtDuration(s: number | null): string {
  if (s === null) return "—";
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}m ${r}s`;
}

export default function Investigations() {
  const list = useInvestigations(50);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const detail = useInvestigation(selectedId);

  useEffect(() => {
    if (selectedId === null && list.data && list.data.items.length > 0) {
      setSelectedId(list.data.items[0].id);
    }
  }, [selectedId, list.data]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Investigations</h1>
        <p className="text-sm text-muted-foreground">
          Custom analyses Tyrael ran on demand from chat ("evaluate cambridge.ro",
          "what happened to elvada.ro emails?"). Narrative-only — these don't
          produce structured findings; their scope is whatever you asked.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[280px,1fr]">
        <Card className="p-0 overflow-hidden">
          <div className="border-b border-border bg-muted/40 px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            History
          </div>
          {list.isLoading && (
            <div className="px-4 py-6 text-sm text-muted-foreground">Loading…</div>
          )}
          {list.data?.items.length === 0 && (
            <div className="px-4 py-6 text-sm text-muted-foreground">
              No investigations yet. Talk to Tyrael in Telegram with a custom question
              ("tyrael, what happened to <span className="font-mono">elvada.ro</span> mail today?")
              and it'll show up here.
            </div>
          )}
          <div className="max-h-[70vh] overflow-y-auto">
            {list.data?.items.map((inv) => {
              const active = inv.id === selectedId;
              return (
                <button
                  key={inv.id}
                  type="button"
                  onClick={() => setSelectedId(inv.id)}
                  className={cn(
                    "block w-full border-b border-border px-4 py-3 text-left transition-colors last:border-0",
                    active ? "bg-primary/5" : "hover:bg-muted/40",
                  )}
                >
                  <div className="text-xs text-muted-foreground">
                    #{inv.id} · {fmtDateTime(inv.run_at)} · {fmtDuration(inv.duration_s)}
                  </div>
                  {inv.prompt_teaser && (
                    <div className="mt-1 line-clamp-2 text-xs font-medium text-foreground">
                      {inv.prompt_teaser}
                    </div>
                  )}
                  <div className="mt-1 line-clamp-2 text-[11px] text-muted-foreground">
                    {inv.teaser}
                  </div>
                </button>
              );
            })}
          </div>
        </Card>

        <Card>
          {!selectedId && (
            <div className="text-sm text-muted-foreground">Pick an investigation on the left.</div>
          )}
          {selectedId && detail.isLoading && (
            <div className="text-sm text-muted-foreground">Loading…</div>
          )}
          {detail.data && (
            <>
              <div className="mb-4 border-b border-border pb-3">
                <div className="text-xs uppercase tracking-wider text-muted-foreground">
                  Investigation #{detail.data.id} · {fmtDateTime(detail.data.run_at)} · {fmtDuration(detail.data.duration_s)}
                </div>
                <div className="mt-1 rounded-md border border-border bg-muted/30 px-3 py-2 text-sm">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Question</div>
                  <div className="mt-0.5">{detail.data.prompt}</div>
                </div>
              </div>
              <AuditMarkdown body={detail.data.body} />
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
